import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { UniversalLauncher } from "@theg1group/global-search-ui";
import { isG1App } from "@theg1group/pwa-kit/pure";
import { useAuth } from "@theg1group/rbac-ui";
import { AppsRegistryProvider } from "@theg1group/ui";
import { RequireAuth } from "./RequireAuth.js";
import { ShellSidebar } from "./ShellSidebar.js";
import { ShellTopbar } from "./ShellTopbar.js";
import { useShellNav } from "./useShellNav.js";
const COLLAPSED_KEY = "g1_sidebar_collapsed";
function useCollapsed() {
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem(COLLAPSED_KEY) === "1";
        }
        catch {
            return false;
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
        }
        catch {
            // ignore
        }
    }, [collapsed]);
    return [collapsed, () => setCollapsed((c) => !c)];
}
/**
 * The shared operator shell. Renders the ⌘G launcher, the auth gate, and the
 * responsive chrome (mobile drawer + desktop rail sidebar + topbar) with nav
 * built from `FeatureModule.nav` — replacing each app's bespoke `Layout.tsx`.
 *
 * Mount it inside the app's `BrowserRouter` + `QueryClientProvider` +
 * rbac-ui `AuthProvider` + `PagesRegistryProvider` (those carry the app's own
 * `fetchUser` / `fetchRegistry`). Import `@theg1group/operator-theme` once in
 * the app's CSS so the token utilities resolve.
 */
export function AppShell({ appId, features, branding, notifications, topbarActions, authUrl, require2FA = true, requireAppAccess, launcher = true, appsSource = "runtime", appsRegistryEndpoint, collapsibleGroups = false, children, }) {
    const { user } = useAuth();
    const groups = useShellNav(features);
    const [collapsed, toggleCollapsed] = useCollapsed();
    const [mobileOpen, setMobileOpen] = useState(false);
    const closeMobile = () => setMobileOpen(false);
    // Pull the design tokens at runtime: a palette change at <auth>/theme.css
    // re-themes every operator app on next load — no rebuild/redeploy. The bundled
    // operator-theme :root is the offline fallback; this <link> is appended after
    // the bundle so its :root wins the cascade (equal specificity → later wins).
    useEffect(() => {
        const id = "g1-runtime-theme";
        if (typeof document === "undefined" || document.getElementById(id))
            return;
        const base = authUrl ?? "https://auth.g1.network";
        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.href = `${base}/theme`;
        document.head.appendChild(link);
    }, [authUrl]);
    const brandIcon = branding.Icon ? _jsx(branding.Icon, { size: 20 }) : undefined;
    // Native G1 iOS shell: cross-app navigation is the native launcher's job, so
    // the ⌘G modal stays unmounted (its app list would duplicate the native one).
    const nativeShell = typeof navigator !== "undefined" && isG1App(navigator.userAgent);
    return (_jsxs(AppsRegistryProvider, { source: appsSource, endpoint: appsRegistryEndpoint, children: [launcher && !nativeShell && (_jsx(UniversalLauncher, { currentAppId: appId, appAccess: user?.appAccess ? [...user.appAccess] : undefined })), _jsx(RequireAuth, { authUrl: authUrl, require2FA: require2FA, requireAppAccess: requireAppAccess, children: _jsxs("div", { className: "flex min-h-screen bg-canvas text-content", children: [mobileOpen && (_jsx("button", { type: "button", className: "fixed inset-0 z-30 bg-black/50 md:hidden", onClick: closeMobile, "aria-label": "Close menu" })), _jsx(ShellSidebar, { groups: groups, title: branding.title, brandIcon: brandIcon, collapsed: collapsed, collapsibleGroups: collapsibleGroups, mobileOpen: mobileOpen, onCloseMobile: closeMobile, onToggleCollapse: toggleCollapsed }), _jsxs("div", { className: "flex min-w-0 flex-1 flex-col", children: [_jsx(ShellTopbar, { title: branding.title, onOpenMobile: () => setMobileOpen(true), notifications: notifications, actions: topbarActions }), _jsx("main", { className: "min-w-0 flex-1 overflow-auto bg-canvas pb-[env(safe-area-inset-bottom)]", children: children })] })] }) })] }));
}
//# sourceMappingURL=AppShell.js.map